import React, { useEffect, useMemo, useState } from 'react';
import { Loader, ImageOff, Camera } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { customerPortalService } from '../../services/customerPortalApi';
import SearchableSelect from '../common/SearchableSelect';
import DigitalPrintingCreateOrderModal from './DigitalPrintingCreateOrderModal';
import CustomerPatternVariantModal from './CustomerPatternVariantModal';
import AdvancedImageSearch from '../production/AdvancedImageSearch';
import { BaskiGorselComponent } from '../production/BaskiGorselLazyImage';

const sortPatternCode = (a, b) => {
  const parse = (code) => {
    if (!code) return { base: '', variant: '' };
    const [base, variant] = String(code).split('-');
    const baseNum = parseInt(base.replace(/^\D+/g, ''), 10);
    const variantNum = parseInt(variant || '', 10);
    return {
      baseNum: Number.isNaN(baseNum) ? Number.MAX_SAFE_INTEGER : baseNum,
      baseText: base || '',
      variantNum: Number.isNaN(variantNum) ? Number.MAX_SAFE_INTEGER : variantNum,
      variantText: variant || ''
    };
  };

  const pa = parse(a.desenNo);
  const pb = parse(b.desenNo);

  // Desen numarasının sayısal kısmı için: büyükten küçüğe (JJ12 > JJ11 > JJ10)
  if (pa.baseNum !== pb.baseNum) {
    return pb.baseNum - pa.baseNum;
  }

  // Aynı sayısal kısımda text kısmını da büyükten küçüğe sırala
  if (pa.baseText !== pb.baseText) {
    return pb.baseText.localeCompare(pa.baseText);
  }

  // Aynı desen için varyantlar: küçükten büyüğe (1,2,3,...)
  if (pa.variantNum !== pb.variantNum) {
    return pa.variantNum - pb.variantNum;
  }

  return pa.variantText.localeCompare(pb.variantText);
};

const DigitalPrintingCustomerPatternList = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [patterns, setPatterns] = useState([]);
  const [stockCards, setStockCards] = useState([]);
  const [selectedStockCardId, setSelectedStockCardId] = useState('');
  const [search, setSearch] = useState('');
  const [error, setError] = useState('');
  const [selectedPattern, setSelectedPattern] = useState(null);
  const [isOrderModalOpen, setIsOrderModalOpen] = useState(false);
  const [variantPattern, setVariantPattern] = useState(null);
  const [isVariantModalOpen, setIsVariantModalOpen] = useState(false);
  const [showVisualSearch, setShowVisualSearch] = useState(false);

  useEffect(() => {
    const loadInitial = async () => {
      try {
        setLoading(true);
        const [patternRes, stockRes] = await Promise.all([
          customerPortalService.get('/digital-printing/patterns'),
          customerPortalService.get('/stock-cards/active')
        ]);

        if (patternRes.data?.success) {
          setPatterns(patternRes.data.data || []);
        } else {
          setPatterns([]);
        }

        const stockPayload = stockRes.data;
        const stockArray = Array.isArray(stockPayload)
          ? stockPayload
          : Array.isArray(stockPayload?.data)
          ? stockPayload.data
          : [];
        setStockCards(stockArray);
      } catch (e) {
        console.error('Müşteri desenleri yüklenemedi:', e);
        setError('Desenler yüklenemedi');
      } finally {
        setLoading(false);
      }
    };

    loadInitial();
  }, []);

  const filteredPatterns = useMemo(() => {
    let list = [...patterns];

    if (selectedStockCardId) {
      list = list.filter(
        (p) => String(p.stockCardId) === String(selectedStockCardId)
      );
    }

    if (search.trim()) {
      const term = search.trim().toLowerCase();
      list = list.filter(
        (p) =>
          (p.desenNo || '').toLowerCase().includes(term) ||
          (p.modelNo || '').toLowerCase().includes(term)
      );
    }

    return list.sort(sortPatternCode);
  }, [patterns, selectedStockCardId, search]);

  const handlePatternClick = (pattern) => {
    setSelectedPattern(pattern);
    setIsOrderModalOpen(true);
  };

  const handleModalClose = () => {
    setIsOrderModalOpen(false);
    setSelectedPattern(null);
  };

  const handleOrderSuccess = () => {
    setIsOrderModalOpen(false);
    setSelectedPattern(null);
    navigate('/orders?tab=pending');
  };

  const handleAddVariant = (pattern) => {
    setVariantPattern(pattern);
    setIsVariantModalOpen(true);
  };

  const handleVariantModalClose = () => {
    setVariantPattern(null);
    setIsVariantModalOpen(false);
  };

  const handleVariantSuccess = (newPattern) => {
    if (newPattern) {
      setPatterns((prev) => [...prev, newPattern]);
    }
    setVariantPattern(null);
    setIsVariantModalOpen(false);
  };

  const handleVisualSearchSelect = (desen) => {
    const pattern = {
      id: desen.id,
      desenNo: desen.desenNo || desen.desen_no,
      modelNo: desen.modelNo || null,
      stockCardId: desen.stockCardId || desen.stok_kart_id || null,
      gorselUrls: desen.gorselUrls
        || (Array.isArray(desen.gorsel_urls) ? desen.gorsel_urls : [])
    };
    setShowVisualSearch(false);
    handlePatternClick(pattern);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col md:flex-row gap-3 md:items-center md:justify-between">
        <div>
          <h3 className="text-base font-semibold text-gray-900 dark:text-white">
            Desenlerim
          </h3>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Baskı siparişi oluşturmak için önce desen seçin.
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 w-full md:w-auto">
          <div className="sm:w-64">
            <SearchableSelect
              options={stockCards}
              value={selectedStockCardId}
              onChange={(e) => setSelectedStockCardId(e.target.value)}
              placeholder="Kumaşa göre filtrele"
              searchPlaceholder="Kumaş ara..."
              getOptionLabel={(c) => c.name}
              getOptionValue={(c) => c.id}
              name="patternStockCardFilter"
              isClearable
            />
          </div>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Desen no veya model no ara"
            className="w-full sm:w-56 border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
          />
          <button
            type="button"
            onClick={() => setShowVisualSearch(true)}
            className="inline-flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700 whitespace-nowrap"
          >
            <Camera className="h-4 w-4" />
            Görselle Ara
          </button>
        </div>
      </div>

      {showVisualSearch && (
        <AdvancedImageSearch
          variant="customer"
          apiClient={customerPortalService}
          uploadUrl="/digital-printing/patterns/search-by-image"
          title="Görselle Ara"
          subtitle="Giysideki kumaş desenine odaklanır; kendi baskılarınızda benzerleri arar"
          selectLabel="Bu desenle sipariş ver"
          showAdminSiparis={false}
          textFilterPlaceholder="Örn: model no, kumaş adı…"
          onClose={() => setShowVisualSearch(false)}
          onSelectDesen={handleVisualSearchSelect}
        />
      )}

      {loading && (
        <div className="flex items-center justify-center py-8 text-sm text-gray-500 dark:text-gray-400">
          <Loader className="w-4 h-4 mr-2 animate-spin" />
          Desenler yükleniyor...
        </div>
      )}

      {!loading && error && (
        <div className="text-sm text-red-600 dark:text-red-400">{error}</div>
      )}

      {!loading && !error && filteredPatterns.length === 0 && (
        <div className="flex flex-col items-center justify-center py-10 text-center text-sm text-gray-500 dark:text-gray-400">
          <ImageOff className="w-8 h-8 mb-2 text-gray-400" />
          <p>Görüntülenecek desen bulunamadı.</p>
        </div>
      )}

      {!loading && !error && filteredPatterns.length > 0 && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
            {filteredPatterns.map((pattern) => {
              const stockCard =
                stockCards.find(
                  (c) => String(c.id) === String(pattern.stockCardId)
                ) || null;
              const stockCardName = stockCard?.name || '';
              const imageUrl =
                Array.isArray(pattern.gorselUrls) && pattern.gorselUrls.length > 0
                  ? pattern.gorselUrls[0]
                  : null;

              return (
                <div
                  key={pattern.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => handlePatternClick(pattern)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handlePatternClick(pattern);
                    }
                  }}
                  className="group bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow flex flex-col text-left"
                >
                  <div className="aspect-square bg-gray-100 dark:bg-gray-800 flex items-center justify-center overflow-hidden">
                    {imageUrl ? (
                      <div className="w-full h-full group-hover:scale-105 transition-transform">
                        <BaskiGorselComponent
                          imageUrl={imageUrl}
                          alt={pattern.desenNo || 'Desen'}
                          useThumbnail
                          thumbnailSize={200}
                          priority={false}
                        />
                      </div>
                    ) : (
                      <ImageOff className="w-6 h-6 text-gray-400" />
                    )}
                  </div>
                  <div className="px-2.5 py-2 space-y-0.5">
                    <div className="flex items-center justify-between gap-1">
                      {pattern.modelNo ? (
                        <p className="text-xs font-semibold text-gray-900 dark:text-white truncate">
                          {pattern.modelNo}
                        </p>
                      ) : (
                        <span />
                      )}
                      <p className="text-xs font-semibold text-gray-900 dark:text-white flex-shrink-0">
                        {pattern.desenNo}
                      </p>
                    </div>
                    <div className="flex items-center justify-between gap-2">
                      {stockCardName ? (
                        <p className="text-[11px] text-gray-700 dark:text-gray-300 truncate">
                          {stockCardName}
                        </p>
                      ) : (
                        <span />
                      )}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleAddVariant(pattern);
                        }}
                        className="text-[11px] text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 font-medium flex-shrink-0"
                      >
                        Varyant Ekle
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <DigitalPrintingCreateOrderModal
            isOpen={isOrderModalOpen}
            onClose={handleModalClose}
            onSuccess={handleOrderSuccess}
            allPatterns={patterns}
            stockCardsFromParent={stockCards}
            initialPattern={
              selectedPattern
                ? {
                    ...selectedPattern,
                    imageUrl:
                      Array.isArray(selectedPattern.gorselUrls) &&
                      selectedPattern.gorselUrls.length > 0
                        ? selectedPattern.gorselUrls[0]
                        : null
                  }
                : null
            }
          />

          <CustomerPatternVariantModal
            isOpen={isVariantModalOpen}
            onClose={handleVariantModalClose}
            onSuccess={handleVariantSuccess}
            pattern={variantPattern}
            stockCards={stockCards}
          />
        </>
      )}
    </div>
  );
};

export default DigitalPrintingCustomerPatternList;

